import { Callout, DefaultButton, MessageBar, MessageBarType, Pivot, PivotItem, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchExpedite } from '../../api';
import { searchQuery } from '../../api/elasticsearch';
import { Market } from '../../api/markets.types';
import { fetchProvider } from '../../api/providers';
import { AsyncData, onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { RootState } from '../../redux/types';
import { useQueryString } from '../../util/hooks';
import { AsyncVisitSidePanel } from '../core/AsyncVisitSidePanel';
import CopyButton from '../core/CopyButton';
import { DateSelector } from '../core/DateSelector';
import { DoctorSelector } from '../core/DoctorSelector';
import { HealHqMap, pinSymbol, Point } from '../core/HealHqMap';
import { PageHeader } from '../core/PageHeader';
import { PageSidePanel } from '../core/PageSidePanel';
import { VisitList } from '../core/VisitList';

// TODO Update any to Visit type - consolidate all visit interfaces
export const ProviderVisits = () => {
  const dispatch = useDispatch();
  const [tab, setTab] = useQueryString('tab', 'list');

  const [providerId, setProviderId] = useQueryString('providerId', '');
  const [date, setDate] = useQueryString('date', moment().format('YYYY-MM-DD'));
  const [groupCode, setGroupCode] = useState<string>();

  const [providerHomeLocation, setProviderHomeLocation] = useState<google.maps.LatLngLiteral>();
  const [providerHomeAddress, setProviderHomeAddress] = useState<string>();
  const [allVisits, setAllVisits] = useState<any[]>([]);
  const [visits, setVisits] = useState<any[]>([]);
  const [fetching, setFetching] = useState(false);
  const [selectedVisitCode, setSelectedVisitCode] = useState('');
  const [expedited, setExpedited] = useState<string[]>([]);

  const markets = useSelector<RootState, AsyncData<Market[]>>((state) => state.markets.markets);

  const visitCodes = visits.map((v) => v.visitCode);
  const amVisits = visits.filter((v) => v.longTimeString.indexOf('am') > -1).map((v) => `${v.addressLong}`);
  const pmVisits = visits.filter((v) => v.longTimeString.indexOf('am') === -1).map((v) => `${v.addressLong}`);

  useEffect(() => {
    dispatch(onPageDidMount('ProviderVisits'));
    return () => {
      dispatch(onPageWillUnmount('ProviderVisits'));
    };
  }, [dispatch]);

  useEffect(() => {
    let canceled = false;
    setAllVisits([]);
    setProviderHomeLocation(undefined);

    if (providerId) {
      setFetching(true);

      // This is searching for visits where the doctor id is explicitly assigned OR the visit is unassigned, but the recommendation is set.
      const p1 = searchQuery(
        `long_date_string:"${moment(date).format('MMM D, YYYY')}" AND ((NOT _exists_:doctor_id AND recommended_doctor:"${providerId}") OR doctor_id:"${providerId}")`
      ).then((data) => {
        if (!canceled) {
          setAllVisits(data);
        }
      });
      const p2 = fetchProvider(providerId).then((data) => {
        const providerHome = (data.provider.addressResponses || []).find((a) => a.type === 'HOME');
        if (providerHome) {
          setProviderHomeLocation({ lat: providerHome.latitude, lng: providerHome.longitude });
          setProviderHomeAddress(providerHome.addressLine1 + ', ' + providerHome.city + ' ' + providerHome.state + ' ' + providerHome.zipcode);
        }
      });

      Promise.allSettled([p1, p2]).then(() => setFetching(false));
    }
    return () => {
      canceled = true;
    };
  }, [providerId, date]);

  useEffect(() => {
    const visitsOnDate = allVisits
      .filter((v) => moment(v.longerDateString).isSame(moment(date), 'day'))
      .filter((v) => ['TELEMED_CHILD', 'TELEMED_ADULT', 'THERAPY_CHILD', 'THERAPY_ADULT'].indexOf(v.serviceCode) === -1)
      .filter((v) => ['CANCELLED', 'FAILED_PREAUTH'].indexOf(v.status) === -1 || v.cancelReasonId === 302); // 302: Provider went but patient not home
    setVisits(_.sortBy(visitsOnDate, 'timeStartedVisit', 'recommendedTime', 'scheduledTime'));

    // Uglyness to get a group code
    const marketCode = visitsOnDate.filter((v) => !v.serviceCode.startsWith('TELE') && !v.serviceCode.startsWith('THERAPY'))[0]?.marketCode;
    if (marketCode && markets.data) {
      setGroupCode(markets.data?.find((m) => m.code === marketCode)?.groupCode);
    } else {
      setGroupCode(undefined);
    }
  }, [markets.data, allVisits, date]);

  useEffect(() => {
    if (groupCode && providerId && date && moment().startOf('day').isSameOrBefore(moment(date))) {
      fetchExpedite(groupCode, date, providerId).then(setExpedited);
    }
  }, [groupCode, date, providerId]);

  const columns = ['insurance', 'code', 'status', 'patient', 'address', 'service', 'timeslot', 'eta', 'scheduled-start', 'duration', 'assistant'];

  const mapItOnGoogle = (mode?: string) => {
    // AM visits - provider home address, all AM visits
    // PM visits - last visit in AM as START, all PM visits, provider home address
    //           - if there are no AM visits, then use home as START
    let portions;
    if (mode === 'am') {
      portions = [providerHomeAddress, ...amVisits];
    } else if (mode === 'pm' && amVisits.length > 0) {
      portions = [amVisits[amVisits.length - 1], ...pmVisits, providerHomeAddress];
    } else {
      portions = [providerHomeAddress, ...amVisits, ...pmVisits, providerHomeAddress];
    }

    window.open('https://www.google.com/maps/dir/' + portions.join('/'));
  };

  const splitOptions = {
    items: [
      {
        key: 'mapAM',
        text: 'Map AM visits',
        iconProps: { iconName: 'Sunny' },
        disabled: amVisits.length === 0,
        onClick: () => mapItOnGoogle('am'),
      },
      {
        key: 'mapPM',
        text: 'Map PM visits',
        iconProps: { iconName: 'ClearNight' },
        disabled: pmVisits.length === 0,
        onClick: () => mapItOnGoogle('pm'),
      },
    ],
  };

  return (
    <>
      <PageHeader title="Providers Visits" showShareButton={true} />

      <Stack horizontal={true} horizontalAlign="start" verticalAlign="end" tokens={{ childrenGap: 10 }}>
        <DoctorSelector label="Doctor" value={providerId} onChange={(id) => setProviderId(id)} />
        <DateSelector outputFormat="YYYY-MM-DD" defaultDate={moment(date).toDate()} showNudgeButtons={true} onChange={(d) => setDate(d)} />
        <DefaultButton text="Map on Google" split menuProps={splitOptions} onClick={() => mapItOnGoogle()} disabled={!providerHomeLocation || visits.length === 0} />
        <CopyButton
          text={[providerHomeAddress, ...amVisits, ...pmVisits, providerHomeAddress].join('\n')}
          message={`${visitCodes.length} addresses were copied to the clipboard`}
          disabled={!providerHomeLocation || visits.length === 0}
          tooltip="Copies the provider home address, list of address, and provider home address to your clipboard."
        />
        <ExpediteButton visits={visits} original={visitCodes} expedited={expedited} providerHomeAddress={providerHomeAddress} />
      </Stack>
      {!fetching && !providerHomeLocation && (
        <MessageBar styles={{ root: { marginTop: 10 } }} messageBarType={MessageBarType.warning}>
          This provider does not have a home address configured. Map on Google is disabled.
        </MessageBar>
      )}

      <Pivot selectedKey={tab} onLinkClick={(i) => setTab(i?.props.itemKey || 'list')}>
        <PivotItem headerText="List" itemKey="list">
          <VisitList selectedVisitCode={selectedVisitCode} visits={visits as any[]} columns={columns} isFetching={false} markets={markets} onSelected={setSelectedVisitCode} />
        </PivotItem>
        <PivotItem headerText="Map" itemKey="map">
          <VisitMapItems visits={visits} selected={selectedVisitCode} homeLocation={providerHomeLocation} onClick={setSelectedVisitCode} />
        </PivotItem>
      </Pivot>

      {fetching && <Spinner size={SpinnerSize.large} label="Fetching provider visits..." />}
      {!fetching && visits.length === 0 && <MessageBar messageBarType={MessageBarType.info}>There are no visits on this date.</MessageBar>}

      {selectedVisitCode && (
        <PageSidePanel>
          <AsyncVisitSidePanel visitCode={selectedVisitCode} onClose={() => setSelectedVisitCode('')} />
        </PageSidePanel>
      )}
    </>
  );
};

const VisitMapItems = ({
  visits,
  selected,
  homeLocation,
  onClick,
}: {
  visits: any[];
  selected: string;
  homeLocation?: google.maps.LatLngLiteral;
  onClick: (code: string) => void;
}) => {
  const points: Point[] = visits.map((v) => {
    const isSelected = v.visitCode === selected;
    return {
      lat: v.addressLatitude || v.latitudeAddress,
      lng: v.addressLongitude || v.longitudeAddress,
      selected: isSelected,
      icon: isSelected ? 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png' : 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
      toolTip: isSelected ? v.visitCode : undefined,
      onClick: () => onClick(v.visitCode),
    };
  });
  let center = { lat: 34.0522, lng: -118.2437 }; // LA
  if (points.length > 0) {
    center = points.find((p) => p.selected) || points[0];
  }

  if (homeLocation) {
    points.push({
      ...homeLocation,
      icon: pinSymbol('#234', '#234'),
      toolTip: 'Home',
    });
  }

  return <HealHqMap center={homeLocation ? homeLocation : center} height={700} points={points} />;
};

const ExpediteButton = ({ visits, original, expedited, providerHomeAddress }: { visits: any[]; original: string[]; expedited: string[]; providerHomeAddress?: string }) => {
  const [open, setOpen] = useState(false);

  const showExpeditedRoute = () => {
    const allVisits = visits.sort((a: any, b: any) => expedited.indexOf(a.visitCode) - expedited.indexOf(b.visitCode)).map((v) => `${v.addressLong}`);
    const portions = [providerHomeAddress, ...allVisits, providerHomeAddress];
    window.open('https://www.google.com/maps/dir/' + portions.join('/'));
  };

  return (
    <>
      <DefaultButton className="expedite-target" onClick={() => setOpen(!open)} text="Expedite" disabled={visits.length === 0 || !providerHomeAddress} />
      {open && (
        <Callout gapSpace={0} target={`.expedite-target`} onDismiss={() => setOpen(!open)} setInitialFocus>
          <MessageBar>The expedited order of visits is calculated using a traveling salesmen problem solver with no restrictions on time.</MessageBar>
          <table width="100%" style={{ margin: 20, marginBottom: 60 }}>
            <thead>
              <tr>
                <td>Original</td>
                <td>Address</td>
                <td>Expedited</td>
                <td>Address</td>
              </tr>
            </thead>
            <tbody>
              {original.map((value, i) => {
                return (
                  <tr key={i}>
                    <td>{value}</td>
                    <td>{visits.find((v) => v.visitCode === value)?.addressLong || 'missing address'}</td>
                    <td>{expedited[i]}</td>
                    <td>{visits.find((v) => v.visitCode === expedited[i])?.addressLong || 'missing address'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <DefaultButton
            style={{ position: 'absolute', right: 20, bottom: 20 }}
            text="Map expedited visits..."
            onClick={showExpeditedRoute}
            disabled={expedited.length !== visits.length}
          />
        </Callout>
      )}
    </>
  );
};
